/**
 * The pairing code, on screen, twice.
 *
 * The QR is the fast path; the token AS SELECTABLE TEXT WITH A COPY BUTTON is
 * the one that has to work anyway. That is not a nicety: a camera that will
 * not focus is a support call, and the scanner's own pairing view ships a
 * manual-entry field for exactly this case (phase 4's PairDevice.jsx offers
 * "Code von Hand eingeben" next to the scanner). Pairing must not depend on a
 * single code path, on either side of the glass.
 *
 * SHOWN ONCE, STORED NOWHERE. The token lives in the parent's component state
 * and dies with it -- the devices panel unmounts on a tab change, which clears
 * it, and there is no cache entry, no storage (ESLint bans both storages) and
 * no console line anywhere on this path. The API makes the same promise from
 * the other end: `createPairingCode` stores only SHA-256(token), so a lost
 * code can only be replaced, never re-read.
 */
import { useEffect, useState } from 'react';
import { toDataURL } from 'qrcode';
import { buildPairingPayload } from '../lib/qr';
import { pairingCountdown } from '../lib/staffView';
import { t } from '../lib/i18n';

export interface PairingQrProps {
  token: string;
  expiresAt: string;
  onClose: () => void;
}

export function PairingQr({ token, expiresAt, onClose }: PairingQrProps) {
  // THE QR CONTENT. `buildPairingPayload` returns the token byte-identical;
  // nothing between here and the canvas may add to it (see lib/qr.ts).
  const payload = buildPairingPayload(token);

  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [qrFailed, setQrFailed] = useState(false);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    let cancelled = false;
    setQrFailed(false);
    toDataURL(payload, { errorCorrectionLevel: 'M', margin: 2, width: 256 })
      .then((url) => {
        if (!cancelled) setDataUrl(url);
      })
      .catch(() => {
        // Never logged: the argument that failed IS the credential. The text
        // half of this panel still works, which is the whole reason it exists.
        if (!cancelled) setQrFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [payload]);

  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const countdown = pairingCountdown(expiresAt, nowMs);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(payload);
      setCopyState('copied');
    } catch {
      // Insecure context, a browser without the API, or a denied permission.
      // The token is already on screen and selectable, so this is a hint, not
      // a failure -- and, again, nothing about it is logged.
      setCopyState('failed');
    }
  }

  return (
    <section className="pairing-qr" aria-label={t('staff.pair.title')}>
      <h3>{t('staff.pair.title')}</h3>
      <p className="pairing-once">{t('staff.pair.once')}</p>

      {countdown.expired ? (
        <p className="pairing-expired" role="alert">{countdown.label}</p>
      ) : (
        <p className="pairing-countdown" role="status">{countdown.label}</p>
      )}

      {qrFailed ? (
        <p className="pairing-qr-failed">{t('staff.pair.qrFailed')}</p>
      ) : dataUrl === null ? (
        <p className="pairing-qr-pending">{t('common.loading')}</p>
      ) : (
        <img className="pairing-qr-image" src={dataUrl} alt={t('staff.pair.qrAlt')} width={256} height={256} />
      )}
      <p className="pairing-scan-hint">{t('staff.pair.scanHint')}</p>

      <p className="pairing-manual-hint">{t('staff.pair.manualHint')}</p>
      {/* Selectable, monospaced, and the exact string the scanner's manual
          field expects -- the same `payload`, not a re-derived one. */}
      <code className="pairing-token">{payload}</code>
      <button type="button" onClick={() => void handleCopy()}>
        {t('staff.pair.copy')}
      </button>
      {copyState === 'copied' ? <span className="pairing-copied" role="status">{t('staff.pair.copied')}</span> : null}
      {copyState === 'failed' ? <span className="pairing-copy-failed">{t('staff.pair.copyFailed')}</span> : null}

      <button type="button" onClick={onClose}>
        {t('staff.pair.close')}
      </button>
    </section>
  );
}
